import React, { useState } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { 
  generateDashaEvolutionReport, 
  DashaEvolutionPhase, 
  DashaTransitionBridge 
} from '../utils/dashaEvolutionEngine';
import { 
  Sparkles, 
  Compass, 
  Clock, 
  ArrowRight, 
  Shield, 
  TrendingUp, 
  Brain, 
  Award, 
  CheckCircle2, 
  Flame, 
  Zap, 
  ChevronRight, 
  Layers, 
  Calendar 
} from 'lucide-react';

interface DashaEvolutionTimelineViewProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const DashaEvolutionTimelineView: React.FC<DashaEvolutionTimelineViewProps> = ({
  kundali,
  lang = 'en'
}) => {
  const [conversationalLang, setConversationalLang] = useState<'hinglish' | 'hi' | 'en'>('hinglish');
  const [selectedLevel, setSelectedLevel] = useState<'mahadasha' | 'antardasha' | 'pratyantardasha'>('mahadasha');
  
  const report = generateDashaEvolutionReport(kundali);

  const initialMahaIdx = Math.max(0, report.allChronologicalPhases.findIndex(p => p.isCurrent));
  const [selectedMahaIdx, setSelectedMahaIdx] = useState<number>(initialMahaIdx);
  const [selectedAntarIdx, setSelectedAntarIdx] = useState<number>(0);
  const [selectedPdIdx, setSelectedPdIdx] = useState<number>(0);

  const activeMaha = report.allChronologicalPhases[selectedMahaIdx] || report.activePhaseSummary;
  const antardashaList = activeMaha.antardashas || [];
  const activeAntar = antardashaList[selectedAntarIdx] || antardashaList[0] || activeMaha;
  const pdList = activeAntar.pratyantardashas || [];
  const activePd = pdList[selectedPdIdx] || pdList[0] || activeAntar;

  // Determine current active phase and displayed ribbon list based on selected level
  let displayedPhases: DashaEvolutionPhase[] = [];
  let selectedPhaseIdx = 0;
  let activePhase: DashaEvolutionPhase = activeMaha;

  if (selectedLevel === 'mahadasha') {
    displayedPhases = report.allChronologicalPhases;
    selectedPhaseIdx = selectedMahaIdx;
    activePhase = activeMaha;
  } else if (selectedLevel === 'antardasha') {
    displayedPhases = antardashaList;
    selectedPhaseIdx = selectedAntarIdx;
    activePhase = activeAntar;
  } else {
    displayedPhases = pdList;
    selectedPhaseIdx = selectedPdIdx;
    activePhase = activePd;
  }

  const setSelectedPhaseIdx = (idx: number) => {
    if (selectedLevel === 'mahadasha') {
      setSelectedMahaIdx(idx);
      setSelectedAntarIdx(0);
      setSelectedPdIdx(0);
    } else if (selectedLevel === 'antardasha') {
      setSelectedAntarIdx(idx);
      setSelectedPdIdx(0);
    } else {
      setSelectedPdIdx(idx);
    }
  };

  const currentMaha = report.currentMahadasha;
  const currentAntar = report.currentAntardasha;
  const nextMaha = report.nextMahadasha;

  const getPlanetColor = (planet: string) => {
    switch (planet) {
      case 'Sun': return 'from-amber-500 to-orange-600 text-white';
      case 'Moon': return 'from-sky-400 to-indigo-500 text-white';
      case 'Mars': return 'from-red-500 to-rose-700 text-white';
      case 'Mercury': return 'from-emerald-500 to-teal-600 text-white';
      case 'Jupiter': return 'from-amber-400 to-yellow-600 text-astro-primary';
      case 'Venus': return 'from-pink-400 to-rose-500 text-white';
      case 'Saturn': return 'from-blue-700 to-slate-800 text-white';
      case 'Rahu': return 'from-purple-600 to-slate-900 text-white';
      case 'Ketu': return 'from-orange-700 to-amber-900 text-white';
      default: return 'from-astro-primary to-astro-text text-white';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-br from-astro-primary to-[#0D2D2C] text-white p-5 sm:p-7 rounded-2xl shadow-md space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#E6C687]" />
              <span className="text-xs uppercase tracking-widest font-bold text-[#E6C687]">
                Dasha Evolution & Life Phase Transition Engine
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-white mt-1">
              {conversationalLang === 'hi' 
                ? 'दशा-आधारित जीवन रूपांतरण एवं चेतना विकास'
                : (conversationalLang === 'hinglish'
                    ? 'Dasha-Driven Personality Shift & Life Evolution'
                    : 'Dasha-Driven Life Phases & Executive Evolution')}
            </h2>
            <p className="text-xs text-white/80 mt-1 max-w-2xl">
              {conversationalLang === 'hi'
                ? 'जानिए कैसे आपकी महादशाएं आपके स्वभाव, जोखिम लेने की क्षमता और नेतृत्व शैली को रूपांतरित करती हैं।'
                : (conversationalLang === 'hinglish'
                    ? 'Dekhein kaise aapka leadership mindset, risk appetite aur daily focus alag-alag Dashas mein dynamically badalta hai.'
                    : 'Discover how planetary cycles systematically reshape your behavioral drive, risk orientation, and executive mandate.')}
            </p>
          </div>

          {/* Language Switcher */}
          <div className="flex items-center gap-1 bg-white/10 p-1 rounded-xl border border-white/20 self-start sm:self-auto shrink-0">
            <button
              onClick={() => setConversationalLang('hinglish')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                conversationalLang === 'hinglish'
                  ? 'bg-white text-astro-primary shadow-sm'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Hinglish (Natural)
            </button>
            <button
              onClick={() => setConversationalLang('hi')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                conversationalLang === 'hi'
                  ? 'bg-white text-astro-primary shadow-sm'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              हिंदी
            </button>
            <button
              onClick={() => setConversationalLang('en')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                conversationalLang === 'en'
                  ? 'bg-white text-astro-primary shadow-sm'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              English
            </button>
          </div>
        </div>

        {/* Current Active Dasha Quick Status Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-white/15 text-xs">
          <div className="bg-white/10 backdrop-blur-xs p-3 rounded-xl border border-white/15">
            <span className="text-[10px] uppercase font-bold text-[#E6C687] block">
              Active Mahadasha:
            </span>
            <span className="text-sm font-bold text-white block mt-0.5">
              {currentMaha?.planet || 'Jupiter'} Mahadasha
            </span>
            <span className="text-[10px] text-white/70">
              {currentMaha?.startDate} to {currentMaha?.endDate}
            </span>
          </div>

          <div className="bg-white/10 backdrop-blur-xs p-3 rounded-xl border border-white/15">
            <span className="text-[10px] uppercase font-bold text-[#E6C687] block">
              Active Antardasha:
            </span>
            <span className="text-sm font-bold text-white block mt-0.5">
              {currentAntar?.planet ? `${currentAntar.planet} Sub-Period` : 'In Progress'}
            </span>
            <span className="text-[10px] text-white/70">
              {currentAntar?.startDate} to {currentAntar?.endDate}
            </span>
          </div>

          <div className="bg-white/10 backdrop-blur-xs p-3 rounded-xl border border-white/15">
            <span className="text-[10px] uppercase font-bold text-[#E6C687] block">
              Incoming Next Cycle:
            </span>
            <span className="text-sm font-bold text-white block mt-0.5">
              {nextMaha?.planet ? `${nextMaha.planet} Mahadasha` : 'Completion Arc'}
            </span>
            <span className="text-[10px] text-white/70">
              Starts: {nextMaha?.startDate || 'Upcoming'}
            </span>
          </div>
        </div>
      </div>

      {/* Dasha Transition Shifts Matrix (Highlighting how style transforms) */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-astro-accent" />
          <h3 className="text-lg font-serif font-bold text-astro-primary">
            {conversationalLang === 'hi' 
              ? 'दशा परिवर्तन सूत्र: सोच और कार्यशैली में रूपांतरण'
              : (conversationalLang === 'hinglish'
                  ? 'Key Dasha Transitions: Orientation Shift & Executive Mindset'
                  : 'Major Dasha Transition Bridges')}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {report.upcomingTransitions.map((bridge, idx) => (
            <div 
              key={idx}
              className="bg-astro-bg rounded-2xl border-2 border-astro-accent/40 p-5 shadow-xs space-y-3.5 flex flex-col justify-between"
            >
              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-2 border-b border-astro-border pb-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-md bg-astro-primary text-white text-[11px] font-bold">
                      {bridge.fromPlanet}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-astro-accent" />
                    <span className="px-2 py-0.5 rounded-md bg-astro-accent text-white text-[11px] font-bold">
                      {bridge.toPlanet}
                    </span>
                  </div>
                  <span className="text-[11px] font-bold text-astro-secondary-text bg-astro-card px-2 py-0.5 rounded-md border border-astro-border">
                    {bridge.transitionDate}
                  </span>
                </div>

                <h4 className="font-serif font-bold text-astro-primary text-sm sm:text-base">
                  {conversationalLang === 'hi'
                    ? bridge.transitionTitleHi
                    : (conversationalLang === 'hinglish' ? bridge.transitionTitleHinglish : bridge.transitionTitleEn)}
                </h4>

                {/* Explicit Conversational Quote requested by user */}
                <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 relative">
                  <div className="text-xs text-astro-text font-medium leading-relaxed italic">
                    "{bridge.orientationShiftQuote[conversationalLang]}"
                  </div>
                </div>

                {/* Before vs After States */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-1">
                  <div className="bg-astro-card p-2.5 rounded-lg border border-astro-border space-y-1">
                    <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">
                      ⏪ Previous Focus ({bridge.fromPlanet}):
                    </span>
                    <p className="text-[#384342] text-[11px] leading-snug">
                      {bridge.beforeState[conversationalLang]}
                    </p>
                  </div>

                  <div className="bg-astro-primary/5 p-2.5 rounded-lg border border-astro-primary/20 space-y-1">
                    <span className="text-[10px] font-bold text-astro-primary uppercase tracking-wider block">
                      ⏩ Future Mindset ({bridge.toPlanet}):
                    </span>
                    <p className="text-astro-primary font-medium text-[11px] leading-snug">
                      {bridge.afterState[conversationalLang]}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Advice Pill */}
              <div className="pt-2 border-t border-astro-border flex items-start gap-2 text-xs">
                <CheckCircle2 className="w-4 h-4 text-astro-accent shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-astro-primary text-[11px] block">
                    {conversationalLang === 'hi' ? 'मार्गदर्शन:' : (conversationalLang === 'hinglish' ? 'Thos Guidance & Strategy:' : 'Strategic Advice:')}
                  </span>
                  <p className="text-xs text-[#384342]">
                    {bridge.actionAdvice[conversationalLang]}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chronological Phase Selector Strip & Level Hierarchy */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-astro-accent" />
            <h3 className="text-base sm:text-lg font-serif font-bold text-astro-primary">
              {conversationalLang === 'hi' ? 'दशा पदानुक्रम एवं जीवन चक्र' : (conversationalLang === 'hinglish' ? 'Dasha Timeline & Phase Inspector' : 'Dasha Lifecycle & Hierarchy')}
            </h3>
          </div>
          
          {/* Level Hierarchy Switcher */}
          <div className="flex items-center gap-1 bg-[#F2EDE4] p-1 rounded-xl border border-astro-border">
            <button
              onClick={() => setSelectedLevel('mahadasha')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                selectedLevel === 'mahadasha'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              Mahadasha (Macro)
            </button>
            <button
              onClick={() => setSelectedLevel('antardasha')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                selectedLevel === 'antardasha'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              Antardasha (Tactical)
            </button>
            <button
              onClick={() => setSelectedLevel('pratyantardasha')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                selectedLevel === 'pratyantardasha'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              Pratyantardasha (Micro)
            </button>
          </div>
        </div>

        {/* Horizontal Phase Selector Ribbon */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {displayedPhases.map((phase, idx) => {
            const isSelected = selectedPhaseIdx === idx;
            const label = phase.level === 'mahadasha'
              ? phase.mahadashaLord
              : (phase.level === 'antardasha'
                  ? `${phase.antardashaLord}`
                  : `${phase.pratyantardashaLord}`);

            const subLabel = phase.level === 'mahadasha'
              ? `${phase.startDate.split('-')[0]} – ${phase.endDate.split('-')[0]}`
              : `${phase.startDate.slice(5)} to ${phase.endDate.slice(5)}`;

            return (
              <button
                key={idx}
                onClick={() => setSelectedPhaseIdx(idx)}
                className={`px-3.5 py-2.5 rounded-xl border text-xs font-medium transition-all shrink-0 cursor-pointer text-left flex flex-col justify-between min-w-[110px] ${
                  isSelected
                    ? 'bg-astro-primary text-white border-astro-primary shadow-sm'
                    : (phase.isCurrent
                        ? 'bg-[#FAF6ED] text-astro-primary border-astro-accent ring-1 ring-astro-accent'
                        : 'bg-astro-bg text-astro-secondary-text border-astro-border hover:border-astro-primary')
                }`}
              >
                <div className="flex items-center justify-between gap-1.5">
                  <span className="font-bold text-xs">
                    {label}
                  </span>
                  {phase.isCurrent && (
                    <span className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold uppercase ${
                      isSelected ? 'bg-[#E6C687] text-astro-primary' : 'bg-astro-primary text-white'
                    }`}>
                      Active
                    </span>
                  )}
                </div>
                <span className="text-[10px] opacity-80 mt-1">
                  {subLabel}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detailed Inspection Card of Selected Phase (Populating 4-Box Grid) */}
      <div className="bg-astro-bg rounded-2xl border border-astro-border p-5 sm:p-6 shadow-xs space-y-4">
        {/* Active Phase Header Card */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-3.5">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getPlanetColor(activePhase.pratyantardashaLord || activePhase.antardashaLord || activePhase.mahadashaLord)} flex items-center justify-center font-bold text-base shadow-xs shrink-0`}>
              {(activePhase.pratyantardashaLord || activePhase.antardashaLord || activePhase.mahadashaLord).substring(0, 2)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-serif font-bold text-base sm:text-lg text-astro-primary">
                  {conversationalLang === 'hi'
                    ? activePhase.phaseTitleHi
                    : (conversationalLang === 'hinglish' ? activePhase.phaseTitleHinglish : activePhase.phaseTitleEn)}
                </h4>
                {activePhase.isCurrent && (
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full border border-emerald-300">
                    Currently Active
                  </span>
                )}
              </div>
              <span className="text-xs text-astro-secondary-text">
                Duration: {activePhase.startDate} to {activePhase.endDate}
              </span>
            </div>
          </div>
        </div>

        {/* 4-Box Metric Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 text-xs">
          {/* Box 1: Mindset & Psychology */}
          <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
            <span className="font-bold text-astro-primary flex items-center gap-1.5 text-[11px] uppercase tracking-wide">
              <Brain className="w-3.5 h-3.5 text-astro-accent" />
              {conversationalLang === 'hi' ? 'मानसिक स्वभाव एवं चेतना' : (conversationalLang === 'hinglish' ? 'Mindset & Psychology in This Phase' : 'Dominant Psychology')}
            </span>
            <p className="text-[#384342] text-xs leading-relaxed">
              {activePhase.dominantPsychology[conversationalLang]}
            </p>
          </div>

          {/* Box 2: Leadership Style & Command Level */}
          <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
            <span className="font-bold text-astro-primary flex items-center gap-1.5 text-[11px] uppercase tracking-wide">
              <Award className="w-3.5 h-3.5 text-astro-accent" />
              {conversationalLang === 'hi' ? 'नेतृत्व शैली एवं प्रभाव' : (conversationalLang === 'hinglish' ? 'Leadership Style & Command Level' : 'Leadership & Agency')}
            </span>
            <p className="text-[#384342] text-xs leading-relaxed">
              {activePhase.leadershipAndAgency[conversationalLang]}
            </p>
          </div>

          {/* Box 3: Risk Appetite & Wealth Behavior */}
          <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
            <span className="font-bold text-astro-primary flex items-center gap-1.5 text-[11px] uppercase tracking-wide">
              <TrendingUp className="w-3.5 h-3.5 text-astro-accent" />
              {conversationalLang === 'hi' ? 'जोखिम क्षमता एवं धन नीति' : (conversationalLang === 'hinglish' ? 'Risk Appetite & Wealth Behavior' : 'Risk Appetite & Capital')}
            </span>
            <p className="text-[#384342] text-xs leading-relaxed">
              {activePhase.riskAppetiteAndWealth[conversationalLang]}
            </p>
          </div>

          {/* Box 4: Daily Focus & Operational Work */}
          <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
            <span className="font-bold text-astro-primary flex items-center gap-1.5 text-[11px] uppercase tracking-wide">
              <Layers className="w-3.5 h-3.5 text-astro-accent" />
              {conversationalLang === 'hi' ? 'दैनिक प्राथमिकताएं एवं कर्म' : (conversationalLang === 'hinglish' ? 'Daily Focus & Operational Work' : 'Daily Operational Focus')}
            </span>
            <p className="text-[#384342] text-xs leading-relaxed">
              {activePhase.dailyOperationalFocus[conversationalLang]}
            </p>
          </div>
        </div>

        {/* Bottom Banner: Core Transformation Takeaway */}
        <div className="bg-astro-primary/5 border border-astro-primary/20 p-3.5 rounded-xl flex items-start gap-2.5">
          <Sparkles className="w-4 h-4 text-astro-primary shrink-0 mt-0.5" />
          <div>
            <span className="font-bold text-astro-primary text-[11px] uppercase tracking-wide block">
              {conversationalLang === 'hi' ? 'इस काल का मुख्य सार एवं रूपांतरण:' : (conversationalLang === 'hinglish' ? 'Core Transformation Takeaway:' : 'Key Evolution Arc:')}
            </span>
            <p className="text-xs text-astro-text mt-0.5 leading-relaxed">
              {activePhase.keyTransformationNarrative[conversationalLang]}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
